import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAuth from "../../../../hooks/useAuth";
import useAxios from "../../../../hooks/useAxios";
import { __clientChangeNumber } from "../../../../api/sim";
import { isPartnerIccidDigits } from "../../../../utils/value";

/** First column: 19–20 digits = ICCID; 10 digits = MSISDN. */
const lineMode = (firstColumn) => {
  const d = String(firstColumn).replace(/\D/g, "");
  if (d.length === 19 || d.length === 20) return "iccid";
  if (d.length === 10) return "msisdn";
  if (d.length === 11 && d.startsWith("1")) return "msisdn";
  return null;
};

const normalizeMsisdn = (raw) => {
  let d = String(raw).replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  return d;
};

const ClientChangeNumberForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [formData, setFormData] = useState(null);

  const { user } = useAuth();
  /** Without a virtual plan, only ICCID lines (890124039…) are allowed — no MSISDN column. */
  const restrictToVirtualIccidOnly =
    user && !user.admin && user.virtual_plan_set === false;

  const { setAlertMessage } = useAlert();
  const initialData = { lines: [] };
  const [response, call, loading] = useAxios({
    ...__clientChangeNumber,
  });
  const nav = useNavigate();

  const optionalLocationFields = {
    areacode: yup
      .string("areacode")
      .test("Digits only", "The field should have 3 digits only", (value) =>
        value && value.length ? /^\d{3}$/.test(value) : true
      )
      .optional(),
    zipcode: yup
      .string("zipcode")
      .test("Digits only", "The field should have 5 digits only", (value) =>
        value && value.length ? /^\d{5}$/.test(value) : true
      )
      .optional(),
  };

  const lineValidationSchemaIccid = yup.object({
    iccid: yup
      .string("iccid")
      .min(19, "Should be exactly 19 or 20 digits long")
      .max(20, "Should be exactly 19 or 20 digits long")
      .required(),
    ...optionalLocationFields,
  });

  const lineValidationSchemaMsisdn = yup.object({
    msisdn: yup
      .string("msisdn")
      .matches(/^\d{10}$/, "MSISDN must be 10 digits")
      .required(),
    ...optionalLocationFields,
  });

  useEffect(() => {
    if (formData) {
      call({
        data: {
          lines: [...linesData],
        },
      });
    }
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({
        type: "success",
        message: "Change number queue started",
      });
      setLinesData([]);
      nav("/client/change_number");
    }
    formik.setSubmitting(false);
  }, [response]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];

    if (!data || !String(data).trim()) {
      setLinesData([]);
      return errors;
    }

    const lines = String(data)
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      setLinesData([]);
      return errors;
    }

    const firstParts = lines[0].split(",").map((p) => p.trim());
    const batchMode = lineMode(firstParts[0]);
    if (!batchMode) {
      errors.lines = restrictToVirtualIccidOnly
        ? "First column must be a 19–20 digit ICCID starting with 890124039."
        : "First column must be a 19–20 digit ICCID or a 10-digit MSISDN.";
      return errors;
    }

    if (restrictToVirtualIccidOnly && batchMode !== "iccid") {
      errors.lines =
        "Changing number by phone number (MSISDN) requires a T-Mobile J virtual plan on your account. Use ICCID lines only (19–20 digits starting with 890124039), or contact an administrator.";
      return errors;
    }

    lines.some((line, idx) => {
      const parts = line.split(",").map((p) => p.trim());
      if (parts.length < 1 || parts.length > 3) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      const firstCol = parts[0];
      const rowMode = lineMode(firstCol);
      if (rowMode !== batchMode) {
        errors.lines =
          "All lines must use ICCIDs only or MSISDNs only in the first column — do not mix.";
        return true;
      }

      try {
        let obtainedData;
        if (batchMode === "iccid") {
          const iccidDigits = firstCol.replace(/\D/g, "");
          if (restrictToVirtualIccidOnly && !isPartnerIccidDigits(iccidDigits)) {
            errors.lines = `Invalid ICCID in line ${
              idx + 1
            }: must be 19–20 digits starting with 890124039.`;
            return true;
          }
          obtainedData =
            parts.length === 1
              ? { iccid: iccidDigits }
              : parts.length === 2
              ? { iccid: iccidDigits, areacode: parts[1] }
              : {
                  iccid: iccidDigits,
                  areacode: parts[1],
                  zipcode: parts[2],
                };
          lineValidationSchemaIccid.validateSync(obtainedData);
        } else {
          const msisdn = normalizeMsisdn(firstCol);
          obtainedData =
            parts.length === 1
              ? { msisdn }
              : parts.length === 2
              ? { msisdn, areacode: parts[1] }
              : { msisdn, areacode: parts[1], zipcode: parts[2] };
          lineValidationSchemaMsisdn.validateSync(obtainedData);
        }

        formatedLines.push(obtainedData);
      } catch (err) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        obj[key] = values[key] || null;
        return obj;
      }, {});

      setFormData(requestData);
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };
  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    // Check if user has entered the file
    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors.lines,
          upload: "Please input a csv file",
        });
        return;
      }

      handleParse(inputFile);
    }
  };

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Change Number sims"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.upload) ? true : false}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors.upload) ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <InputLabel htmlFor="lines">
              {
                "One entry per line. Optional area code (3 digits) and zip (5 digits), comma-separated."
              }
              <br />
              {restrictToVirtualIccidOnly ? (
                <>
                  {
                    "Use ICCIDs only: 19–20 digits starting with 890124039 (virtual inventory). MSISDN is not available until a virtual plan is assigned."
                  }
                  <br />
                  {"Example: 89012403901234567890,212,10001"}
                </>
              ) : (
                <>
                  {
                    "Use either ICCIDs (19–20 digits) or MSISDNs (10-digit phone) in the first column — not mixed in the same request."
                  }
                  <br />
                  {"Examples: 89012608531234567890,212,10001 or 5551234567,212,10001"}
                </>
              )}
              <br />
              <em>
                {"Areacode is 3 digits; zipcode is 5 digits."}
              </em>
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors.lines) ? true : false}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={
                  restrictToVirtualIccidOnly
                    ? `ICCID (890124039…),[Areacode],[Zipcode]\n89012403901234567890,212,10001`
                    : `ICCID or MSISDN,[Areacode],[Zipcode]\n89012608531234567890,212\n5551234567,212,10001`
                }
                style={{ minWidth: 550 }}
                name="lines"
                minRows={5}
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
              />
              {Boolean(formik.errors.lines) ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={formik.errors.lines || !linesData.length}
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Change Number SIMs"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ClientChangeNumberForm;
